import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {RedirectService, StorageService, UserHttpService} from '@core/services';
import {needsBasicProfile} from '@core/utils/needs-basic-profile.util';
import {User} from '@capacitor-firebase/authentication';

@Injectable({
  providedIn: 'root',
})
export class NoAuthGuard {
  private redirectService = inject(RedirectService);
  private userHttpService = inject(UserHttpService);
  private router = inject(Router);
  private storageService = inject(StorageService);

  private async redirectHome(route: ActivatedRouteSnapshot) {
    const nextPageUrl = route.queryParamMap.get('nextPageUrl');
    if (nextPageUrl) {
      await this.redirectService.setRedirectUrl(nextPageUrl);
    }
    const redirectUrl = await this.redirectService.getAndClearRedirectUrl();
    if (redirectUrl) {
      return this.router.parseUrl(redirectUrl);
    }
    return this.router.createUrlTree(['/home-tabs']);
  }

  public async canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    try {
      const user = await this.storageService.get('user') as User | null;
      if (user?.uid) {
        const cached = await this.userHttpService.readCachedSession();
        if (cached && !needsBasicProfile(cached)) {
          this.userHttpService.openFromCache(user, cached);
          return this.redirectHome(route);
        }
        const clientUser = await this.userHttpService.ensureSession();
        if (!clientUser) {
          return true;
        }
        if (needsBasicProfile(clientUser)) {
          return this.router.createUrlTree(['/set-basic-profile']);
        }
        return this.redirectHome(route);
      }
      return true;
    } catch (error) {
      console.error('Error during authentication guard', error);
      return true;
    }
  }
}
